import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { formatINRShort } from "../../utils/crmDealValue";
import CrmForecastPanel from "./CrmForecastPanel";

const CrmInsights = ({ days = 30, compact = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(days);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${url}/leads/crm-analytics?days=${range}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, url, token]);

  if (loading && !stats) {
    return <p className="crm-insights__muted">Loading CRM insights…</p>;
  }

  if (!stats) {
    return <p className="crm-insights__muted">Could not load CRM insights.</p>;
  }

  const { totals, funnel, sources, repLeaderboard, forecast, lossReasonBreakdown } = stats;
  const maxFunnel = Math.max(...(funnel || []).map((f) => f.count), 1);

  return (
    <div className={`crm-insights ${compact ? "crm-insights--compact" : ""}`}>
      <div className="crm-insights__header">
        <strong>CRM insights</strong>
        <select
          value={range}
          onChange={(e) => setRange(Number(e.target.value))}
          className="crm-insights__select"
          aria-label="Date range"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="crm-insights__kpis">
        <div className="crm-insights__kpi">
          <span className="crm-insights__kpi-label">Conversion</span>
          <span className="crm-insights__kpi-value">{totals.conversionRate}%</span>
          <span className="crm-insights__kpi-sub">
            {totals.converted} / {totals.total} leads
          </span>
        </div>
        <div className="crm-insights__kpi">
          <span className="crm-insights__kpi-label">Open pipeline</span>
          <span className="crm-insights__kpi-value">
            {formatINRShort(totals.openPipelineValue) || "₹0"}
          </span>
          <span className="crm-insights__kpi-sub">{totals.openCount} active deals</span>
        </div>
        <div className="crm-insights__kpi">
          <span className="crm-insights__kpi-label">Avg response</span>
          <span className="crm-insights__kpi-value">
            {totals.avgResponseMinutes != null
              ? totals.avgResponseMinutes >= 60
                ? `${Math.floor(totals.avgResponseMinutes / 60)}h ${totals.avgResponseMinutes % 60}m`
                : `${totals.avgResponseMinutes}m`
              : "—"}
          </span>
        </div>
        <div className="crm-insights__kpi">
          <span className="crm-insights__kpi-label">Weighted forecast</span>
          <span className="crm-insights__kpi-value">
            {formatINRShort(totals.weightedForecast) || "₹0"}
          </span>
          <span className="crm-insights__kpi-sub">Probability-adjusted pipeline</span>
        </div>
      </div>

      {!compact && forecast && (
        <div className="crm-insights__section">
          <h4>Revenue forecast</h4>
          <CrmForecastPanel forecast={forecast} />
        </div>
      )}

      {lossReasonBreakdown?.length > 0 && !compact && (
        <div className="crm-insights__section">
          <h4>Loss reasons</h4>
          <div className="crm-insights__sources">
            {lossReasonBreakdown.map((row) => (
              <div key={row.reason} className="crm-insights__source-row">
                <span>{row.reason}</span>
                <span>{row.count} lost</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {funnel?.length > 0 && (
        <div className="crm-insights__section">
          <h4>Pipeline funnel</h4>
          <div className="crm-insights__funnel">
            {funnel.map((row) => (
              <div key={row.stage} className="crm-insights__funnel-row">
                <span className="crm-insights__funnel-label">{row.stage}</span>
                <div className="crm-insights__funnel-bar-wrap">
                  <div
                    className="crm-insights__funnel-bar"
                    style={{ width: `${Math.round((row.count / maxFunnel) * 100)}%` }}
                  />
                </div>
                <span className="crm-insights__funnel-count">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sources?.length > 0 && (
        <div className="crm-insights__section">
          <h4>Lead sources</h4>
          <div className="crm-insights__sources">
            {sources.slice(0, 6).map((src) => (
              <div key={src.name} className="crm-insights__source-row">
                <span>{src.name}</span>
                <span>
                  {src.count} leads · {src.conversionRate}% conv
                  {src.pipelineValue > 0 ? ` · ${formatINRShort(src.pipelineValue)}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {repLeaderboard?.length > 0 && !compact && (
        <div className="crm-insights__section">
          <h4>Rep leaderboard</h4>
          <div className="crm-insights__sources">
            {repLeaderboard.slice(0, 8).map((rep) => (
              <div key={rep.staffId} className="crm-insights__source-row">
                <span>{rep.staffName}</span>
                <span>
                  {rep.assigned} assigned · {rep.converted} won · {rep.conversionRate}% conv
                  {rep.avgResponseMinutes != null
                    ? ` · ${rep.avgResponseMinutes}m avg response`
                    : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmInsights;
